import React, { createContext, useState, useContext, useEffect, ReactNode, useRef } from 'react';
import { AuthState, LoginCredentials, RegisterData } from '../types/auth';
import { authService } from '../services/authService';

interface AuthContextType extends AuthState {
  login: (credentials: LoginCredentials) => Promise<void>;
  register: (data: RegisterData) => Promise<void>;
  logout: () => void;
}

const AuthContext = createContext<AuthContextType | undefined>(undefined);

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};

interface AuthProviderProps {
  children: ReactNode;
}

export const AuthProvider: React.FC<AuthProviderProps> = ({ children }) => {
  const [state, setState] = useState<AuthState>({
    user: null,
    token: authService.getToken(),
    isAuthenticated: authService.isAuthenticated(),
    isLoading: true,
  });
  
  const loadingRef = useRef(false);

  useEffect(() => {
    const loadUser = async () => {
      // Prevent multiple calls
      if (loadingRef.current) return;
      loadingRef.current = true;
      
      const token = authService.getToken();
      if (token) {
        try {
          const user = await authService.getProfile();
          setState(prev => ({
            ...prev,
            user,
            token,
            isAuthenticated: true,
            isLoading: false,
          }));
        } catch (error) {
          setState(prev => ({
            ...prev,
            user: null,
            token: null,
            isAuthenticated: false,
            isLoading: false,
          }));
        }
      } else {
        setState(prev => ({ ...prev, isLoading: false }));
      }
      
      loadingRef.current = false;
    };

    loadUser();
  }, []); // Remove state.token dependency to prevent multiple calls

  const login = async (credentials: LoginCredentials) => {
    const { token, user } = await authService.login(credentials);
    setState({
      user,
      token,
      isAuthenticated: true,
      isLoading: false,
    });
  };

  const register = async (data: RegisterData) => {
    const { token, user } = await authService.register(data);
    setState({
      user,
      token,
      isAuthenticated: true,
      isLoading: false,
    });
  };

  const logout = () => {
    authService.logout();
    setState({
      user: null,
      token: null,
      isAuthenticated: false,
      isLoading: false,
    });
  };

  return (
    <AuthContext.Provider value={{ ...state, login, register, logout }}>
      {children}
    </AuthContext.Provider>
  );
};